import React, { useEffect, useMemo, useRef, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import useQueryParam from '../../hooks/useQueryParam';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import useCheckout from '@jwp/ott-hooks-react/src/useCheckout';
import ReactGA from 'react-ga4';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';

import './CleengCheckout.modules.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
// import { clearUTMs } from '@jwp/ott-common/src/utils/utmTracker';

declare global {
  interface Window {
    cleeng?: {
      setAuthTokens: (tokens: { jwt: string; refreshToken: string }) => void;
      onAuthTokensUpdate: (callback: (tokens: { jwt: string; refreshToken: string }) => void) => void;
    };
  }
}

export const CleengCheckout: React.FC = () => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const { isSubscriptionEnabled, isGoogleAnalytics, isAnalyticsEnable, subscription_type } = useAppConfig();
  const offerId = useQueryParam('offerId');
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user.id : 'Guest user';
  const deviceType = true ? useGADeviceType() : null;

  const storedAuth = useMemo(() => {
    try {
      const data = localStorage.getItem('jwapp.auth');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to parse auth from localStorage:', e);
      return null;
    }
  }, []);

  const authToken = storedAuth?.accessToken ?? null;
  const refreshToken = storedAuth?.refreshToken ?? null;

  const publisherId = useMemo(() => {
    try {
      return authToken ? jwtDecode<{ publisherId: string }>(authToken).publisherId : null;
    } catch (error) {
      console.error('JWT decode failed:', error);
      return null;
    }
  }, [authToken]);

  const { selectedOffer } = useCheckout({
    onUpdateOrderSuccess: () => {},
    onSubmitPaymentWithoutDetailsSuccess: () => {},
    onSubmitPaypalPaymentSuccess: () => {},
    onSubmitStripePaymentSuccess: () => {},
    onSubmitPaymentWithoutDetailsFailure: () => {},
    onSubmitPaypalPaymentFailure: () => {},
    onSubmitStripePaymentFailure: () => {},
  });

  useEffect(() => {
    if (!selectedOffer) {
      return;
    }
    if (isAnalyticsEnable) {
      sendGAEvent('paywall_view', {
        asset_id: selectedOffer?.offerId,
        title: selectedOffer?.offerTitle,
        price: selectedOffer?.offerPrice,
        currency: selectedOffer?.offerCurrency,
        provider: subscription_type,
        userid_no: userId,
        device_type: deviceType,
      });
    }
  }, [selectedOffer]);

  useEffect(() => {
    const handleCleengMessage = (event: MessageEvent) => {
      const data = event.data;

      if (data?.type !== 'CLEENG_WIDGET_EVENT') { return; }

      const eventName = data?.payload?.name;
      const payload = data?.payload?.data;

      console.log('CLEENG EVENT →', eventName, payload);

      switch (eventName) {
        case 'checkout-loaded':
          sendGAEvent('checkout_loaded', {
            email: payload.order.customer?.email,
            price: payload.order.offer?.price,
            currency: payload.order.offer?.currency,
            package: payload.order.offer?.title,
          });

          break;

        case 'payment-step-shown':
          console.log(payload, 'payload received');
          break;

        case 'payment-initiated':
          console.log(payload, 'payload received');
          break;

        case 'payment-succeeded':
          // clearUTMs();
          sendGAEvent('payment_success', {
            price: payload.payment?.totalAmount,
            transaction_id: payload.payment?.paymentDetailsId,
            value: payload.payment?.totalAmount,
            currency: payload.payment.currency,
            coupon: payload.payment?.couponId,
            method: payload.payment?.paymentMethod,
            gateway: payload.payment?.paymentGateway,
          });
          break;

        case 'payment-failed':
          sendGAEvent('payment_failure', {
            error_message: payload.error,
          });
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleCleengMessage);
    return () => window.removeEventListener('message', handleCleengMessage);
  }, [authToken, refreshToken]);

  useEffect(() => {
    if (!offerId || !isSubscriptionEnabled) {
      window.location.href = '/';
    }
  }, [offerId]);

  return (
    <>
      <div
        ref={widgetRef}
        data-cleeng-widget="checkout"
        data-cleeng-publisher-id={publisherId}
        data-cleeng-offer-id={offerId}
        // data-cleeng-auth-token={authToken}
        // data-cleeng-refresh-token={refreshToken}
        className="cleeng-checkout-widget"
      />
    </>
  );
};
